import { useCallback, useMemo } from "react";

import { useNavigate } from "react-router";
import useRequest from "@entrilia/shared/hooks/useRequest.ts";
import biApi from "../../../../../../api/biApi.ts";
import type { ReportTemplate, ReportTemplateDto } from "../../../../../../api/types/svTypes.ts";
import CompanyTemplatesDialog from "../../templates/CompanyTemplatesDialog.tsx";
import ReportTemplatesButton from "../../templates/ReportTemplatesButton.tsx";
import { getApplyTemplateUrl, getEditUiReportUrl } from "../utilities/editReportUrl";

interface Props {
  openTemplates: boolean;
  templates: ReportTemplateDto[];
  onSetTemplateOpen: (open: boolean) => void;
  clientCode: string;
  disabled: boolean;
  loading: boolean;
}
export default function ReportTemplates({
  openTemplates,
  templates,
  onSetTemplateOpen,
  clientCode,
  disabled,
  loading,
}: Props) {
  const navigate = useNavigate();
  const { request: createDraftReport, isLoading: isCreatingDraftReport } = useRequest(
    biApi.uiReports.createDraftReport
  );

  const showBadge = useMemo(() => {
    return templates.some((t) => t.isNew);
  }, [templates]);

  const handleQuickUse = useCallback(
    async (template: ReportTemplate) => {
      const [report] = await createDraftReport();
      if (report) {
        navigate(getApplyTemplateUrl(clientCode, report.reportId, template.reportId));
      }
    },
    [clientCode, createDraftReport, navigate]
  );

  const handleCreateBlankReport = useCallback(async () => {
    const [report] = await createDraftReport();
    if (report) {
      navigate(getEditUiReportUrl(clientCode, report.reportId));
    }
  }, [createDraftReport, clientCode, navigate]);

  return (
    <>
      <ReportTemplatesButton
        onClick={() => onSetTemplateOpen(true)}
        showBadge={showBadge}
        disabled={disabled}
        loading={loading}
      />
      {openTemplates && templates && (
        <CompanyTemplatesDialog
          templates={templates}
          isReportCreating={isCreatingDraftReport}
          onClose={() => onSetTemplateOpen(false)}
          createBlankReport={handleCreateBlankReport}
          onQuickUse={handleQuickUse}
        />
      )}
    </>
  );
}
